import { Table, TableContainer, Thead, Tr, Th, Tbody, Td, Box, SkeletonText, Button } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";

import DeleteIcon from "@/assets/DeleteIcon";
import EditIcon from "@/assets/EditIcon";
import ROLE from "@/data/role";
import Pagination from "../Layouts/Pagination";
import { COLORS, USER_STATUS } from "@/common/constants";
import ActiveStatus from "@/assets/ActiveStatus";
import InactiveStatus from "@/assets/InactiveStatus";
import InReviewIcon from "@/assets/InReviewIcon";
import NewIcon from "@/assets/NewIcon";
import RejectedIcon from "@/assets/RejectedIcon";
import EditCompanyIcon from "@/assets/EditCompanyIcon";
import DeleteCompanyIcon from "@/assets/DeleteCompanyIcon";
import ButtonReview from "@/assets/ButtonReview";

type UserItem = {
    id: string;
    userName: string;
    firstName: string;
    lastName: string;
    role: string;
    userType?: string;
    companyName?: string;
    companyId?: string;
    status: number;
    companyStatus: number;
}

type TableUserProps = {
    dataList?: UserItem[];
    isLoading: boolean;
    handleDelete: (id: string, role: string) => void;
    handleRoleBaseDelete: (id: string, role: string) => void;
    handleEdit: (item: UserItem) => void;
    handleViewPrivilege: (userId: string, role: string, firstName: string, lastName: string, companyName: string) => void;
    currentPageSize: number;
    handleChangePageSize: (pageSize: number) => void;
    handleChangePageNumber: (pageNumber: number) => void;
    currentPageNumber: number;
    totalPageNumber: number;
    hasNextPage: boolean;
}

const TableUser = ({
    dataList,
    isLoading,
    handleDelete,
    handleRoleBaseDelete,
    handleEdit,
    handleViewPrivilege,
    currentPageSize,
    handleChangePageSize,
    handleChangePageNumber,
    currentPageNumber,
    totalPageNumber,
    hasNextPage,
}: TableUserProps) => {

    const navigate = useNavigate();
    const NUM_COLUMNS = 11;

    const handleRoleBasedEdit = (item: UserItem) => {
        const state = {
            id: item.companyId,
            returnPath: '/user-management/users',
            source: 'user-management'
        };

        switch (item?.role) {
            case 'shipoperator':
                if (item?.companyId) {
                    navigate('/manage-ship-operator/edit', { state });
                }
                break;
            case 'inspectingcompany':
                if (item?.companyId) {
                    navigate('/manage-inspecting-company/edit', { state });
                }
                break;
            case 'requestor':
                if (item?.companyId) {
                    navigate('/settings/reference-data/edit', { state });
                }
                break;
            default:
                break;
        }
    }

    const showRoleBasedEditButton = (item: UserItem) => {
        return ['shipoperator', 'inspectingcompany', 'requestor'].includes(item.role) && Boolean(item.companyId) && ![USER_STATUS.NEW].includes(item.companyStatus);
    }

    const getRoleBasedEditLabel = (role: string) => {
        switch (role) {
            case 'shipoperator':
                return 'Edit Ship Operator';
            case 'inspectingcompany':
                return 'Edit Inspector';
            case 'requestor':
                return 'Edit Requestor';
            default:
                return 'Edit Data';
        }
    }

    const handleRoleBasedReview = (item: UserItem) => {
        if (!item?.companyId) {
            return;
        }

        const state = {
            id: item.companyId,
            companyId: item.companyId,
            returnPath: '/user-management/users',
            source: 'user-management'
        };

        switch (item?.role) {
            case 'shipoperator':
                navigate('/manage-ship-operator/review', { state });
                break;
            case 'inspectingcompany':
                navigate('/manage-inspecting-company/review', { state });
                break;
            case 'requestor':
                navigate('/settings/reference-data/edit', { state });
                break;
            default:
                break;
        }
    }

    const renderUserStatus = (userStatus: number) => {
        switch (userStatus) {
            case USER_STATUS.INITIAL:
                return <div style={{ color: 'grey', backgroundColor: 'lightgrey', padding: '4px 20px', borderRadius: '20px', textAlign: 'center', border: '1px solid grey' }}>Initial</div>
            case USER_STATUS.NEW:
                return <NewIcon />
            case USER_STATUS.IN_REVIEW:
                return <InReviewIcon />
            case USER_STATUS.ACTIVE:
                return <ActiveStatus />
            case USER_STATUS.REJECTED:
                return <RejectedIcon />
            case USER_STATUS.INACTIVE:
                return <InactiveStatus />
            default:
                return 'Unknown Status';
        }
    }

    return (
        <TableContainer bgColor="#fff" borderRadius="13">
            <Box overflowX="auto">
                <Table variant='simple'>
                    <Thead>
                        <Tr>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>User Name</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>First Name</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Last Name</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Role</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>User Type</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" textAlign='center' fontSize={14}>Privilege</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Company Name</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" textAlign='center' fontSize={14}>Status</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" textAlign='center' fontSize={14}>Company Status</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" textAlign='center' fontSize={14}>Action for User</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" textAlign='center' fontSize={14}>Action for Company</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {isLoading &&
                            <Tr>
                                <Td colSpan={NUM_COLUMNS}>
                                    <SkeletonText mt='4' noOfLines={6} spacing='4' skeletonHeight='3' />
                                </Td>
                            </Tr>
                        }
                        {(!isLoading && (!dataList || dataList.length === 0)) &&
                            <Tr>
                                <Td colSpan={NUM_COLUMNS} textAlign="center">Data not found</Td>
                            </Tr>
                        }
                        {!isLoading && dataList?.map((item: UserItem) => {
                            return (
                                <Tr key={item.id}>
                                    <Td>{item.userName}</Td>
                                    <Td>{item.firstName}</Td>
                                    <Td>{item.lastName}</Td>
                                    <Td>{ROLE.find((x: { value: string; label: string }) => x.value === item.role)?.label || '-'}</Td>
                                    <Td>{item?.userType ?? '-'}</Td>
                                    <Td textAlign='center'>
                                        <Button onClick={() => handleViewPrivilege(item.id, item.role, item.firstName, item.lastName, item.companyName || '')}>View</Button>
                                    </Td>
                                    <Td>{item.companyName || '-'}</Td>
                                    <Td>
                                        <div style={{ display: 'flex', justifyContent: 'center' }}>{renderUserStatus(item.status)}</div>
                                    </Td>
                                    <Td>
                                        <div style={{ display: 'flex', justifyContent: 'center' }}>{renderUserStatus(item.companyStatus)}</div>
                                    </Td>
                                    <Td style={{ cursor: 'pointer' }}>
                                        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                                            <EditIcon onClick={() => handleEdit(item)} />
                                            <DeleteIcon onClick={() => handleDelete(item.id, item.role)} />
                                        </div>
                                    </Td>
                                    <Td style={{ cursor: 'pointer' }}>
                                        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                                            {showRoleBasedEditButton(item) && (
                                                <EditCompanyIcon onClick={() => handleRoleBasedEdit(item)} label={getRoleBasedEditLabel(item.role)} />
                                            )}
                                            {showRoleBasedEditButton(item) && (
                                                <DeleteCompanyIcon onClick={() => handleRoleBaseDelete(item.companyId || '', item.role)} label={`Delete ${item.role === 'shipoperator' ? 'Ship Operator' : item.role === 'inspectingcompany' ? 'Inspecting Company' : 'Requestor'}`} />
                                            )}
                                            {(['shipoperator', 'inspectingcompany', 'requestor'].includes(item.role) && item.companyStatus === USER_STATUS.NEW) && (
                                                <ButtonReview onClick={() => handleRoleBasedReview(item)} />
                                            )}
                                        </div>
                                    </Td>
                                </Tr>
                            )
                        })}
                    </Tbody>
                </Table>
            </Box>
            <Pagination
                currentPageSize={currentPageSize}
                currentPageNumber={currentPageNumber}
                totalPageNumber={totalPageNumber}
                handleChangePageSize={handleChangePageSize}
                handleChangePageNumber={handleChangePageNumber}
                hasNextPage={hasNextPage}
            />
        </TableContainer>
    )
}

export default TableUser;